import { memo, useCallback } from "react"
import { formatDistanceToNow } from "date-fns"
import { IconTriangleWarningOutline18 as AlertTriangle } from "nucleo-ui-outline-18"

import type { HelpCenterArticleVersion } from "@productlane/zero"
import { Button } from "@productlane/ui"

import { analytics } from "@/lib/analytics"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { useHelpCenterArticleContext } from "./help-center-article-context"
import { restoreArticleVersion } from "./restore-article-version"

interface RestorePreviewBannerProps {
	articleId: string
	version: HelpCenterArticleVersion
	defaultLanguage: string
}

export const RestorePreviewBanner = memo(
	({ articleId, version, defaultLanguage }: RestorePreviewBannerProps) => {
		const { setPreviewVersion } = useHelpCenterArticleContext()
		const zero = useZero()
		const workspace = useZeroWorkspace()

		const createdDate = new Date(version.createdAt)
		const relativeTime = formatDistanceToNow(createdDate, { addSuffix: true })

		const handleCancel = useCallback(() => {
			setPreviewVersion(null)
			analytics.track("cancel-article-version-restore", {
				articleId,
				versionId: version.id,
			})
		}, [setPreviewVersion, articleId, version.id])

		const handleConfirm = useCallback(async () => {
			if (!workspace) return
			const restored = await restoreArticleVersion({
				zero,
				workspaceId: workspace.id,
				articleId,
				version,
				defaultLanguage,
			})
			if (restored) setPreviewVersion(null)
		}, [
			zero,
			workspace,
			articleId,
			version,
			defaultLanguage,
			setPreviewVersion,
		])

		return (
			<div className="z-50 flex-shrink-0 border-b border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
				<div className="flex items-center justify-between gap-4 px-6 py-3">
					<div className="flex items-center gap-3">
						<AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
						<div className="flex flex-col gap-1">
							<p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
								Previewing version from {relativeTime}
							</p>
							<p className="text-xs text-yellow-700 dark:text-yellow-300">
								This is a preview. Your current changes have not been saved yet.
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="sm" onClick={handleCancel}>
							Cancel
						</Button>
						<Button variant="default" size="sm" onClick={handleConfirm}>
							Confirm Restore
						</Button>
					</div>
				</div>
			</div>
		)
	},
)

RestorePreviewBanner.displayName = "RestorePreviewBanner"
